import { keyframes } from '@emotion/react';
import styled from '@emotion/styled';
import { theme } from 'styles/theme/theme';

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-20px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
`;

/**
 * `<dialog>`, открытый `showModal()`, — в верхнем слое браузера, поэтому `z-index` ему не
 * нужен. Растянут на весь экран и прозрачен: белое окно — `ModalContainer` внутри, затемнение
 * — `::backdrop`. Сброшены стили браузера (`padding`, `border`, `max-width/height`, фон, цвет).
 */
export const Dialog = styled.dialog`
  position: fixed;
  inset: 0;
  width: 100%;
  height: 100%;
  max-width: none;
  max-height: none;
  margin: 0;
  padding: 0;
  border: 0;
  background: transparent;
  color: inherit;
  overflow: visible;

  &::backdrop {
    background: ${theme.colors.Scrim};
    animation: ${fadeIn} 0.2s ease-out;
  }
`;

/**
 * Ширина окна задаётся пропом `size` у `UDModal`, а не классами у содержимого.
 * Без `size` окно получает `md` — фиксированную ширину, а не ширину по контенту:
 * иначе окно расползается от длины текста внутри.
 */
export const MODAL_WIDTHS = {
  sm: 440,
  md: 560,
  lg: 760,
} as const;

/**
 * Белое окно. Стоит абсолютно внутри `<dialog>` (он и есть «экран»): по горизонтали — по
 * центру (`left/right: 0` + `margin-inline: auto`), `top` ставит `useModalTop` в `modal-frame.tsx`;
 * `5vh` — только до первого замера.
 *
 * Каркас (IMGM-1623): с `Modal.Body` прямым потомком (или внутри прямого `<form>`) окно само не прокручивается — шапка и
 * `Modal.Footer` закреплены, прокручивается тело (`modal-parts.tsx`). Без `Modal.Body`
 * (свободная сборка) окно прокручивается целиком, как раньше.
 *
 * Высоту плавно меняет `useModalTop` (`modal-frame.tsx`) через `element.animate()`; пока идёт
 * анимация, на окне стоит `data-resizing` — прокрутка выключена, чтобы полоса не мигала.
 */
export const ModalContainer = styled.div<{ $width?: number; $height?: number }>`
  position: absolute;
  top: 5vh;
  left: 0;
  right: 0;
  margin-inline: auto;
  display: flex;
  flex-direction: column;
  ${({ $width }) => ($width ? `width: ${$width}px;` : '')}
  ${({ $height }) => ($height ? `height: ${$height}px;` : '')}
  background: white;
  border: 1px solid ${theme.colors.LineSubtle};
  border-radius: ${({ theme }) => theme.radii.panel};
  max-width: 90vw;
  max-height: 90vh;
  overflow: auto;
  animation: ${slideIn} 0.3s ease-out;

  /* Под @supports не ради браузеров (:has везде с 2023), а ради jsdom 16 в jest: его nwsapi
     падает на :has, и любой getByRole с name внутри окна виснет. Правила @supports jsdom
     пропускает целиком (IMGM-1623, журнал фазы 4). */
  @supports selector(:has(*)) {
    &:has(> .modal-body),
    &:has(> form > .modal-body) {
      overflow: hidden;
    }
  }

  /* Форма на Formik кладёт <form> между окном и Modal.Body. Каждый flex-предок прокручиваемого
     тела тоже должен сжиматься, иначе тело не получит высоту и прокрутится всё окно вместе со
     шапкой (IMGM-1623, журнал фазы 7a). Шапка вне <form> и кнопки внутри — не сжимаются. */
  > form {
    display: flex;
    flex-direction: column;
    min-height: 0;
  }

  &[data-resizing],
  &[data-resizing] > .modal-body,
  &[data-resizing] > form > .modal-body {
    overflow: hidden;
  }

  > .modal-header,
  > .modal-footer,
  > form > .modal-header,
  > form > .modal-footer {
    flex-shrink: 0;
  }

  .close {
    position: absolute;
    top: 10px;
    right: 10px;
    z-index: 1;
  }

  @media (max-width: 768px) {
    max-width: 100vw;
  }
`;
